import React, { Component } from 'react';
import { ProductStatus } from '~/Api/Product';
import Icon from '~/UI/Icon/Icon';

import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';

interface Props {
	status: ProductStatus;
	historicData?: boolean;
	referenceDataSource?: boolean;
}

const IconNames = [null, 'green-clock', 'yellow-question-mark', 'orange-clock', 'red-clock', 'red-exclamation-mark'];

export default class StatusIcon extends Component<Props> {
	render() {
		const statusSeverity = this.resolveStatusSeverity();
		const complianceSeverity = this.resolveComplianceSeverity();
		const iconName = IconNames[Math.max(statusSeverity, complianceSeverity)] ?? null;
		if (iconName === null) {
			return null;
		}

		const icon = this.renderIcon(iconName);
		const statusText = this.renderStatusText();
		const complianceText = this.renderComplianceText();

		return (
			<TailwindTooltip
				reference={
					<>
						{icon}
						<span className='absolute right-full top-0 -mr-1 -mt-1 w-3 h-3 leading-3 rounded-full text-[11px] font-semibold bg-white text-center'>
							?
						</span>
					</>
				}
				displayClass='inline-block mr-2 h-4 align-middle'
				position='left'
			>
				{statusText !== null && statusText}
				{complianceText !== null && complianceText}
			</TailwindTooltip>
		);
	}

	private renderIcon(iconName: string) {
		return <Icon name={iconName} />;
	}

	private resolveStatusSeverity(): number {
		const { status, historicData } = this.props;

		if (historicData === undefined && (status.isRemoved() || status.isBeforeIntroduction())) {
			return 5;
		}
		if (status.isRemoved() && historicData === true) {
			return 5;
		}
		if (status.isRemoved() && historicData === false) {
			return 5;
		}
		if (status.isBeforeIntroduction()) {
			return 5;
		}
		if (status.isDanger()) {
			return 5;
		}
		if (status.isWarning()) {
			return 2;
		}
		if (status.isBeforeValidity()) {
			return 3;
		}
		if (status.isAfterValidity()) {
			return 4;
		}
		return 0;
	}

	private renderStatusText(): string | null {
		const { status, historicData } = this.props;

		if (historicData === undefined && (status.isRemoved() || status.isBeforeIntroduction())) {
			return 'Product is no longer present in current dataset.';
		}
		if (status.isRemoved() && historicData === true) {
			return 'Product is no longer present in the current dataset on the specified date.';
		}
		if (status.isRemoved() && historicData === false) {
			return 'Product is no longer present in current dataset.';
		}
		if (status.isBeforeIntroduction()) {
			return 'Product was not yet present in the current dataset on the specified date';
		}
		if (status.isDanger()) {
			return "This product shouldn't be used for referencing. Please check product details.";
		}
		if (status.isWarning()) {
			return 'This product might not be suitable for referencing. Please make sure this product can be used for your purpose.';
		}
		if (status.isBeforeValidity()) {
			return 'Product has validity that begins in the future and might not be suitable for referencing yet.';
		}
		if (status.isAfterValidity()) {
			return 'Product validity has ended. To assess the product suitability for your needs, please check the product details.';
		}
		return null;
	}

	private resolveComplianceSeverity(): number {
		return this.props.referenceDataSource === false ? 5 : 0;
	}

	private renderComplianceText(): string | null {
		return this.props.referenceDataSource === false
			? 'DataSource of this product is not officially recognized and might not be suitable for price referencing.'
			: null;
	}
}
